import { createFileRoute, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UpsertModelForm } from '@/src/components/manage/mutate/upsertModel'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { listModels, upsertModel } from '@/src/api/model'
import { StatusSwitch } from '@/src/components/ui/status-switch'
import { Label } from '@/src/components/ui/label'
import { Checkbox } from '@/src/components/ui/checkbox'
import { toast } from 'sonner'
import { Spinner } from '@/src/components/ui/spinner'
import { useTranslation } from '@/src/i18n/client'

export const Route = createFileRoute('/_manage/manage/admin/models/$id')({
  component: ModelEditPage,
})

function ModelEditPage() {
  const { t } = useTranslation('manage')
  const { id: modelId } = useParams({ from: '/_manage/manage/admin/models/$id' })
  const queryClient = useQueryClient()

  const { data: models, isLoading, error } = useQuery({
    queryKey: ['models', 'all'],
    queryFn: () => listModels({ data: { all: true } }),
    enabled: modelId !== 'new',
  })

  const model = modelId === 'new' ? null : (models as any)?.find((m: any) => m.id === modelId)

  const toggleEnabledMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!model) return
      await upsertModel({
        data: {
          id: model.id,
          ...model,
          enabled,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models', 'all'] })
      toast.success(t('models.statusUpdated'))
    },
    onError: (err: any) => {
      toast.error(err?.message || t('models.failedToUpdateStatus'))
    },
  })

  const toggleDefaultMutation = useMutation({
    mutationFn: async (isDefault: boolean) => {
      if (!model) return
      await upsertModel({
        data: {
          id: model.id,
          ...model,
          isDefault,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models', 'all'] })
      toast.success(t('models.defaultStatusUpdated'))
    },
    onError: (err: any) => {
      toast.error(err?.message || t('models.failedToUpdateDefaultStatus'))
    },
  })

  const handleEnabledChange = (enabled: boolean) => {
    if (modelId === 'new') return
    toggleEnabledMutation.mutate(enabled)
  }

  const handleDefaultChange = (isDefault: boolean) => {
    if (modelId === 'new') return
    toggleDefaultMutation.mutate(isDefault)
  }

  if (isLoading) {
    return (
      <div>
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title>
              {modelId === 'new' ? t('models.createModel') : t('models.editModel')}
            </AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="max-w-2xl mx-auto p-6">
          <Spinner size={48} />
        </div>
      </div>
    )
  }

  if (modelId !== 'new' && !model) {
    return (
      <div>
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title>{t('models.editModel')}</AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="max-w-2xl mx-auto p-6">
          <div className="text-destructive mb-4">{t('models.modelNotFound')}</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>
            {modelId === 'new' ? t('models.addModel') : t('models.editModel')}
          </AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          {modelId !== 'new' && (
            <div className="flex items-center gap-6">
              <StatusSwitch
                id="model-enabled"
                label={t('models.enabled')}
                checked={model?.enabled ?? true}
                onCheckedChange={handleEnabledChange}
                disabled={isLoading || toggleEnabledMutation.isPending}
              />
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="model-default"
                  checked={model?.isDefault ?? false}
                  onCheckedChange={(checked) => handleDefaultChange(checked as boolean)}
                  disabled={isLoading || toggleDefaultMutation.isPending}
                />
                <Label htmlFor="model-default" className="cursor-pointer text-sm">
                  {t('models.defaultModel')}
                </Label>
              </div>
            </div>
          )}
        </AdminToolbar.Right>
      </AdminToolbar>
      {error ? (
        <div className="text-destructive mb-4 px-6">
          {error instanceof Error ? error.message : t('models.failedToLoadModel')}
        </div>
      ) : (
        <UpsertModelForm
          id={model?.id}
          isNew={modelId === 'new'}
          model={
            model
              ? {
                  id: model.id,
                  name: model.name,
                  provider: model.provider,
                  model: model.model,
                  label: model.label,
                  description: model.description,
                  enabled: model.enabled,
                  isDefault: model.isDefault,
                  function: model.function,
                  permissions: model.permissions,
                  thinkingAmount: model.thinkingAmount,
                  temperature: model.temperature,
                }
              : undefined
          }
          callbackUrl="/manage/admin/models"
        />
      )}
    </div>
  )
}
